/**
 * In-memory SceneMaterial cache for library items.
 *
 * Library items returned by the API carry `fileUrl` (a presigned R2
 * URL) but not the material JSON itself. This module lazily fetches
 * the JSON when a material is first needed by the gallery or by a
 * linked scene block, caches it keyed by libraryItemId, and notifies
 * subscribers when an entry becomes available.
 *
 * Cache semantics:
 *   - undefined  → not yet requested
 *   - "pending"  → fetch in flight
 *   - object     → fetched successfully
 *   - "missing"  → fetch failed (404, network error, malformed JSON)
 *
 * The cache is blown away on logout via `clearMaterialLibraryCache()`.
 */

import type { LibraryItemResponse } from "@poseref/shared";
import type { SceneMaterial } from "../modeling/types.ts";

type CacheEntry =
  | { status: "pending" }
  | { status: "ready"; properties: SceneMaterial }
  | { status: "missing" };

const cache = new Map<string, CacheEntry>();
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version++;
  for (const l of listeners) l();
}

/** Subscribe to cache changes. Returns an unsubscribe function. */
export function subscribeMaterialLibraryCache(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Monotonic counter that bumps on every cache change — suitable as a
 *  stable snapshot for React.useSyncExternalStore. */
export function getMaterialLibraryCacheVersion(): number {
  return version;
}

/** Synchronously read the cached properties for a library material, or null. */
export function getCachedLibraryMaterial(
  libraryId: string,
): SceneMaterial | null {
  const entry = cache.get(libraryId);
  return entry && entry.status === "ready" ? entry.properties : null;
}

/** Write an entry directly — used after export/update so the new JSON is
 *  available immediately without a round-trip back through R2. */
export function setCachedLibraryMaterial(
  libraryId: string,
  properties: SceneMaterial,
): void {
  cache.set(libraryId, { status: "ready", properties: { ...properties } });
  notify();
}

/** Drop the cache — called on logout or when refreshing the library. */
export function clearMaterialLibraryCache(): void {
  cache.clear();
  notify();
}

/**
 * Ensure the JSON for every `material`-typed library item is loaded.
 * Idempotent — already-cached entries are skipped, pending fetches
 * are not duplicated. Safe to call on every library refresh.
 */
export async function ensureLibraryMaterialsLoaded(
  items: LibraryItemResponse[],
): Promise<void> {
  const jobs: Promise<void>[] = [];
  for (const item of items) {
    if (item.type !== "material") continue;
    const entry = cache.get(item.id);
    if (entry) continue;
    jobs.push(fetchOne(item));
  }
  if (jobs.length === 0) return;
  await Promise.allSettled(jobs);
}

async function fetchOne(item: LibraryItemResponse): Promise<void> {
  if (!item.fileUrl) {
    cache.set(item.id, { status: "missing" });
    notify();
    return;
  }
  cache.set(item.id, { status: "pending" });
  notify();
  try {
    const res = await fetch(item.fileUrl);
    if (!res.ok) {
      cache.set(item.id, { status: "missing" });
      notify();
      return;
    }
    const properties = (await res.json()) as SceneMaterial;
    cache.set(item.id, { status: "ready", properties });
  } catch {
    cache.set(item.id, { status: "missing" });
  }
  notify();
}
